import { useEffect, useState } from 'react';
import { apiAutorizacoes, type AutorizacaoDaEster } from '../lib/api.ts';
import { avisar, avisarErro } from './Avisos.tsx';

/**
 * ⭐ "A Ester pede o seu sim" — PRD §60 (19/10/2026).
 *
 * No topo do Hoje, só quando há pedido esperando: é raro, e quando existe é a coisa a decidir.
 * Cada linha tem o resumo que ela escreveu (o caso pelo código, nunca o investigado pelo nome),
 * o código (o mesmo que ela mandou no Telegram) e os dois botões. Aprovar só libera a Ester a
 * fazer aquilo uma vez. Para o colaborador a lista responde 403 e o bloco não aparece.
 *
 * As peças são as da lista do Hoje (`s-folha hoje-lista`, `item item-linha`): nenhuma classe
 * nova, e o comportamento no celular é o da lista de compromissos.
 */
const ROTULO: Record<AutorizacaoDaEster['classe'], string> = {
  apagar: 'Apagar',
  gastar: 'Consulta paga',
  // ⭐ PRD §63.7: o resumo deste é montado pelo Hub (o endereço e o nome de cada arquivo por extenso).
  copia_e_anexo: 'Cópia ou anexo no rascunho',
};

function hora(iso: string): string {
  return new Date(iso).toLocaleString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function AutorizacoesDaEster() {
  const [lista, setLista] = useState<AutorizacaoDaEster[]>([]);
  const [ocupado, setOcupado] = useState<string | null>(null);

  useEffect(() => {
    apiAutorizacoes
      .pendentes()
      .then((r) => setLista(r.filter((a) => !a.vencida)))
      .catch(() => setLista([]));
  }, []);

  async function decidir(a: AutorizacaoDaEster, aprovar: boolean) {
    setOcupado(a.id);
    try {
      await (aprovar ? apiAutorizacoes.aprovar(a.id) : apiAutorizacoes.recusar(a.id));
      setLista((atual) => atual.filter((x) => x.id !== a.id));
      avisar(aprovar ? 'Aprovado: a Ester pode fazer isso uma vez.' : 'Recusado.');
    } catch (e) {
      avisarErro(e);
    } finally {
      setOcupado(null);
    }
  }

  if (lista.length === 0) return null;

  return (
    <section className="s-folha hoje-lista" aria-labelledby="hoje-autorizacoes">
      <div className="s-folha-topo hoje-lista-topo">
        <h2 id="hoje-autorizacoes" className="s-folha-titulo">
          A Ester pede o seu sim
        </h2>
        <span className="s-pilula s-pilula-vinho">{lista.length}</span>
      </div>
      <div className="hoje-itens">
        {lista.map((a) => (
          <div key={a.id} className="item item-linha">
            <div className="item-corpo">
              <p className="item-titulo">{a.resumo}</p>
              <p className="item-meta">
                <span className="etiqueta">{ROTULO[a.classe]}</span>
                <span className="dado">pedido em {hora(a.pedidaEm)}</span>
                <span className="dado">vale até {hora(a.expiraEm)}</span>
                <span className="dado">no Telegram: sim {a.codigo}</span>
              </p>
            </div>
            <div className="item-acoes">
              <button type="button" className="botao-secundario" disabled={ocupado === a.id} onClick={() => void decidir(a, true)}>
                Aprovar
              </button>
              <button type="button" className="botao-texto botao-perigo" disabled={ocupado === a.id} onClick={() => void decidir(a, false)}>
                Recusar
              </button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
